import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { DataTable, Breadcrumbs, BreadcrumbItem } from '../components/ui';
import type { Column } from '../components/ui/DataTable';
import { toast } from '../utils/toast';
import type { PaymentMethod } from '../types';
import {
  Edit2,
  ToggleLeft,
  ToggleRight,
  CreditCard,
  Smartphone,
  Hash,
  WalletCards,
  X,
  CheckCircle2,
} from 'lucide-react';

const MOCK_PAYMENT_METHODS: PaymentMethod[] = [
  {
    id: '1',
    name: 'Transferência Bancária',
    type: 'bank_transfer',
    status: 'active',
    config: {
      iban: 'AO06 0000 0000 0000 0000 0000 0',
      bank_name: 'Banco Teste',
      account_name: 'API Marketplace LDA',
      instructions: 'Faça a transferência e anexe o comprovativo.',
    },
  },
  {
    id: '2',
    name: 'Referência EMIS',
    type: 'emis_reference',
    status: 'active',
    config: { instructions: 'Use a referência gerada no seu Multicaixa.' },
  },
  {
    id: '3',
    name: 'Multicaixa Express',
    type: 'real_time',
    status: 'active',
    config: { instructions: 'Insira o seu número de telemóvel associado ao Multicaixa Express.' },
  },
  {
    id: '4',
    name: 'Afrimoney',
    type: 'real_time',
    status: 'inactive',
    config: { instructions: 'Insira o seu número Afrimoney.' },
  },
  {
    id: '5',
    name: 'Unitel Money',
    type: 'real_time',
    status: 'inactive',
    config: { instructions: 'Insira o seu número Unitel Money.' },
  },
];

export const PaymentMethods: React.FC = () => {
  const queryClient = useQueryClient();
  const [methods, setMethods] = useState<PaymentMethod[]>(MOCK_PAYMENT_METHODS);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: PaymentMethod[] }>('/admin/payment-methods');
        if (res.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setMethods(res.data.data);
          return { data: res.data.data };
        }
      } catch {
        // Fallback to local state
      }
      return { data: methods };
    },
  });

  const paymentMethods = data?.data || methods;

  const toggleMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'active' | 'inactive' }) => {
      setMethods((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
      try {
        await api.put(`/admin/payment-methods/${id}/status`, { status });
      } catch {
        // Local state already updated as fallback
      }
      return { success: true };
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast.success(
        `Método ${variables.status === 'active' ? 'ativado' : 'desativado'} com sucesso!`
      );
    },
    onError: () => {
      toast.error('Erro ao alterar estado do método.');
    },
  });

  const handleToggle = (method: PaymentMethod) => {
    toggleMutation.mutate({
      id: method.id,
      status: method.status === 'active' ? 'inactive' : 'active',
    });
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMethod) return;

    setMethods((prev) => prev.map((m) => (m.id === editingMethod.id ? editingMethod : m)));
    try {
      await api.put(`/admin/payment-methods/${editingMethod.id}`, editingMethod);
    } catch {
      // Local state already updated
    }
    setEditingMethod(null);
    void queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    toast.success('Configuração guardada com sucesso!');
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'bank_transfer':
        return <CreditCard className="w-5 h-5 text-blue-500" />;
      case 'real_time':
        return <Smartphone className="w-5 h-5 text-emerald-500" />;
      case 'emis_reference':
        return <Hash className="w-5 h-5 text-purple-500" />;
      default:
        return <CreditCard className="w-5 h-5 text-primary-500" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'bank_transfer':
        return 'Transferência Bancária';
      case 'real_time':
        return 'Tempo Real (Mobile)';
      case 'emis_reference':
        return 'Referência (Multicaixa)';
      default:
        return type;
    }
  };

  const columns: Column<PaymentMethod>[] = [
    {
      key: 'name',
      header: 'Método de Pagamento',
      render: (method) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
            {getMethodIcon(method.type)}
          </div>
          <div>
            <span className="font-bold text-slate-900 dark:text-white block">{method.name}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">ID: {method.id}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Tipo',
      render: (method) => (
        <span className="text-slate-700 dark:text-slate-300 text-sm font-medium">
          {getTypeLabel(method.type)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (method) => (
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${
            method.status === 'active'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
              : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
          }`}
        >
          {method.status === 'active' && <CheckCircle2 className="w-3.5 h-3.5" />}
          {method.status === 'active' ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (method) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleToggle(method)}
            className={`btn-base btn-sm px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all ${
              method.status === 'active'
                ? 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700'
                : 'btn-primary'
            }`}
            title={method.status === 'active' ? 'Desativar' : 'Ativar'}
          >
            {method.status === 'active' ? (
              <ToggleLeft className="w-4 h-4 text-slate-400" />
            ) : (
              <ToggleRight className="w-4 h-4" />
            )}
            <span>{method.status === 'active' ? 'Desativar' : 'Ativar'}</span>
          </button>

          <button
            onClick={() => setEditingMethod(method)}
            className="btn-base btn-sm btn-ghost text-xs text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/30 px-3 py-1.5 rounded-lg flex items-center gap-1.5"
            title="Configurar"
          >
            <Edit2 className="w-3.5 h-3.5" /> Configurar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <Breadcrumbs className="mb-2">
          <BreadcrumbItem href="/">Dashboard</BreadcrumbItem>
          <BreadcrumbItem>Configurações</BreadcrumbItem>
          <BreadcrumbItem>Métodos de Pagamento</BreadcrumbItem>
        </Breadcrumbs>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
            <WalletCards className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">
              Métodos de Pagamento
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Gerencie os métodos de liquidação disponíveis para os clientes no processo de
              subscrição.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <DataTable
          data={paymentMethods}
          columns={columns}
          isLoading={isLoading}
          emptyTitle="Nenhum método encontrado"
          emptyDescription="Nenhum método de pagamento disponível."
        />
      </div>

      {/* Edit Modal */}
      {editingMethod && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                {getMethodIcon(editingMethod.type)} Configurar {editingMethod.name}
              </h3>
              <button
                type="button"
                onClick={() => setEditingMethod(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveConfig}>
              <div className="p-6 space-y-4">
                {editingMethod.type === 'bank_transfer' && (
                  <>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                        Nome do Banco
                      </label>
                      <input
                        type="text"
                        value={editingMethod.config.bank_name || ''}
                        onChange={(e) =>
                          setEditingMethod({
                            ...editingMethod,
                            config: { ...editingMethod.config, bank_name: e.target.value },
                          })
                        }
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                        Titular da Conta
                      </label>
                      <input
                        type="text"
                        value={editingMethod.config.account_name || ''}
                        onChange={(e) =>
                          setEditingMethod({
                            ...editingMethod,
                            config: { ...editingMethod.config, account_name: e.target.value },
                          })
                        }
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                        IBAN
                      </label>
                      <input
                        type="text"
                        value={editingMethod.config.iban || ''}
                        onChange={(e) =>
                          setEditingMethod({
                            ...editingMethod,
                            config: { ...editingMethod.config, iban: e.target.value },
                          })
                        }
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-mono focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        required
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                    Instruções para o Cliente
                  </label>
                  <textarea
                    value={editingMethod.config.instructions || ''}
                    onChange={(e) =>
                      setEditingMethod({
                        ...editingMethod,
                        config: { ...editingMethod.config, instructions: e.target.value },
                      })
                    }
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                    rows={3}
                    required
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Este texto será exibido no checkout quando o cliente selecionar este método.
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 bg-slate-50/50 dark:bg-slate-800/40">
                <button
                  type="button"
                  onClick={() => setEditingMethod(null)}
                  className="btn-base btn-secondary text-sm font-semibold"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-base btn-primary text-sm font-semibold">
                  Guardar Configuração
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
